// src/pages/MyBookingsPage.jsx

import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import useCockpitGuard from "../hooks/useCockpitGuard";
import BookingStatusBadge from "../components/BookingStatusBadge";

function getSector(booking) {
  return booking.postcode_sector || booking.postcode || "";
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-GB");
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return value;
}

export default function MyBookingsPage() {
  const {
    user,
    businessId,
    loading: authLoading,
  } = useCockpitGuard();

  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;

    if (!user?.id || !businessId) {
      setBookings([]);
      setLoadingBookings(false);
      return;
    }

    let cancelled = false;

    const loadBookings = async () => {
      setLoadingBookings(true);
      setError(null);

      try {
        console.log(
          "[MyBookingsPage] businessId:",
          businessId,
          typeof businessId
        );

        const { data, error: bookingError } = await supabase
          .from("flyer_slots")
          .select(`
            id,
            flyer_id,
            slot_number,
            status,
            created_at,
            offer_title,
            postcode,
            postcode_sector,
            season,
            year,
            business_id
          `)
          .eq("business_id", businessId)
          .order("created_at", {
            ascending: false,
          });

        if (bookingError) {
          throw bookingError;
        }

        if (!cancelled) {
          setBookings(data || []);
        }
      } catch (loadError) {
        console.error("[MyBookingsPage] load error:", loadError);

        if (!cancelled) {
          setBookings([]);
          setError(
            loadError?.message || "Unable to load your bookings."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingBookings(false);
        }
      }
    };

    loadBookings();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.id, businessId]);

  if (authLoading) {
    return (
      <Flex p={{ base: 4, md: 8 }} align="center" gap={3}>
        <Spinner size="sm" color="red.500" />
        <Text>Loading account…</Text>
      </Flex>
    );
  }

  if (!user?.id || !businessId) {
    return (
      <Box p={{ base: 4, md: 8 }}>
        <Button
          as={RouterLink}
          to="/dashboard"
          size="sm"
          variant="outline"
          mb={4}
        >
          ← Dashboard
        </Button>

        <Heading mb={4}>My Bookings</Heading>

        <Box
          borderWidth="1px"
          borderColor="red.200"
          bg="red.50"
          color="red.700"
          borderRadius="md"
          p={4}
        >
          We couldn’t identify your business account.
        </Box>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 8 }} width="100%" overflowX="hidden">
      <Button
        as={RouterLink}
        to="/dashboard"
        size="sm"
        variant="outline"
        mb={4}
      >
        ← Dashboard
      </Button>

      <Flex
        align={{ base: "flex-start", sm: "center" }}
        justify="space-between"
        direction={{ base: "column", sm: "row" }}
        gap={3}
        mb={6}
      >
        <Box>
          <Heading mb={2}>My Bookings</Heading>
          <Text color="gray.600">
            All slots your business has booked across all flyers.
          </Text>
        </Box>

        {!loadingBookings && !error && (
          <Badge colorScheme="red" px={3} py={1} borderRadius="full">
            {bookings.length} {bookings.length === 1 ? "booking" : "bookings"}
          </Badge>
        )}
      </Flex>

      {loadingBookings ? (
        <Flex align="center" gap={3} py={8}>
          <Spinner size="sm" color="red.500" />
          <Text>Loading bookings…</Text>
        </Flex>
      ) : error ? (
        <Box
          borderWidth="1px"
          borderColor="red.200"
          bg="red.50"
          color="red.700"
          borderRadius="md"
          p={4}
        >
          {error}
        </Box>
      ) : bookings.length === 0 ? (
        <Box
          borderWidth="1px"
          borderColor="gray.200"
          bg="white"
          borderRadius="lg"
          p={6}
        >
          <Heading size="sm" mb={2}>
            No bookings yet
          </Heading>
          <Text color="gray.600">
            Booked coupon slots will appear here.
          </Text>
        </Box>
      ) : (
        <>
          {/* Responsive booking cards for mobile and tablet */}
          <SimpleGrid
            display={{ base: "grid", lg: "none" }}
            columns={{ base: 1, sm: 2 }}
            spacing={4}
          >
            {bookings.map((booking) => {
              const sector = getSector(booking);

              return (
                <Box
                  key={booking.id}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="lg"
                  bg="white"
                  p={4}
                  boxShadow="sm"
                  minW={0}
                >
                  <Flex
                    justify="space-between"
                    align="flex-start"
                    gap={3}
                    mb={3}
                  >
                    <Box minW={0}>
                      <Text
                        fontSize="lg"
                        fontWeight="extrabold"
                        color="red.600"
                        noOfLines={1}
                      >
                        {sector || "Unknown postcode"}
                      </Text>

                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.700"
                        noOfLines={2}
                      >
                        {booking.offer_title || "Coupon booking"}
                      </Text>
                    </Box>

                    <Box flexShrink={0}>
                      <BookingStatusBadge status={booking.status} />
                    </Box>
                  </Flex>

                  <SimpleGrid columns={2} spacing={3} mb={4}>
                    <Box>
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        textTransform="uppercase"
                      >
                        Slot
                      </Text>
                      <Text fontWeight="semibold">
                        #{formatValue(booking.slot_number)}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        textTransform="uppercase"
                      >
                        Date
                      </Text>
                      <Text fontWeight="semibold">
                        {formatDate(booking.created_at)}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        textTransform="uppercase"
                      >
                        Season
                      </Text>
                      <Text fontWeight="semibold">
                        {formatValue(booking.season)}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        textTransform="uppercase"
                      >
                        Year
                      </Text>
                      <Text fontWeight="semibold">
                        {formatValue(booking.year)}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  {sector ? (
                    <Button
                      as={RouterLink}
                      to={`/flyer/${encodeURIComponent(sector)}`}
                      size="sm"
                      colorScheme="red"
                      width="100%"
                    >
                      View Flyer
                    </Button>
                  ) : (
                    <Button size="sm" width="100%" isDisabled>
                      Flyer unavailable
                    </Button>
                  )}
                </Box>
              );
            })}
          </SimpleGrid>

          {/* Existing table retained for desktop */}
          <Box
            display={{ base: "none", lg: "block" }}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            bg="white"
            overflowX="auto"
          >
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Postcode</Th>
                  <Th>Offer</Th>
                  <Th>Season</Th>
                  <Th>Year</Th>
                  <Th>Slot</Th>
                  <Th>Status</Th>
                  <Th>Date</Th>
                  <Th textAlign="right">Flyer</Th>
                </Tr>
              </Thead>

              <Tbody>
                {bookings.map((booking) => {
                  const sector = getSector(booking);

                  return (
                    <Tr key={booking.id}>
                      <Td fontWeight="semibold">{sector || "—"}</Td>
                      <Td maxW="220px">
                        <Text noOfLines={1}>
                          {booking.offer_title || "—"}
                        </Text>
                      </Td>
                      <Td>{formatValue(booking.season)}</Td>
                      <Td>{formatValue(booking.year)}</Td>
                      <Td>#{formatValue(booking.slot_number)}</Td>
                      <Td>
                        <BookingStatusBadge status={booking.status} />
                      </Td>
                      <Td>{formatDate(booking.created_at)}</Td>
                      <Td textAlign="right">
                        {sector ? (
                          <Button
                            as={RouterLink}
                            to={`/flyer/${encodeURIComponent(sector)}`}
                            size="xs"
                            colorScheme="red"
                          >
                            View Flyer
                          </Button>
                        ) : (
                          "—"
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}
